import { supabase } from './supabase';

/**
 * Backbar authorization is layered:
 *   1. Env allowlist (bootstrap seed — survives a blank admin_users table)
 *   2. DB `admin_users` row (true source of truth once migration 037 is applied)
 *   3. DB `books_access` row (migration 278) — the outside accountant. Grants
 *      the Books pages only; AuthContext reads it through books_my_access().
 *
 * The session is granted backbar access if ANY check passes. Layers 1 and 2
 * open the whole console; layer 3 alone opens Books + Help and nothing else.
 * A user listed in env but not yet in admin_users is auto-seeded on first
 * sign-in (handled in AuthContext).
 */

const envList = (import.meta.env.VITE_ADMIN_ALLOWLIST as string | undefined) ?? '';
export const ENV_ALLOWLIST = envList
  .split(',')
  .map((s) => s.trim().toLowerCase())
  .filter(Boolean);

export function isEmailEnvAllowed(email: string | null | undefined): boolean {
  if (!email) return false;
  return ENV_ALLOWLIST.includes(email.toLowerCase());
}

export type AdminRow = {
  user_id: string;
  role: 'super_admin' | 'curriculum_lead' | 'data_analyst';
  added_at: string;
};

/** Try to read the caller's admin_users row. Returns null if table missing or no row. */
export async function fetchAdminRow(userId: string): Promise<AdminRow | null> {
  const { data, error } = await supabase
    .from('admin_users')
    .select('user_id,role,added_at')
    .eq('user_id', userId)
    .maybeSingle();
  if (error) {
    // Table may not exist yet (migration 037 not run) — fall back to env allowlist.
    if (error.code === '42P01' || /relation .* does not exist/i.test(error.message)) {
      return null;
    }
    console.warn('[backbar] fetchAdminRow error:', error);
    return null;
  }
  return (data as AdminRow | null) ?? null;
}
